import { colors, styled } from "@mui/material";
import { observer } from "mobx-react";
import * as React from "react";
import Localization from "../../core/Localization";
import Sys from "../../core/Sys";
import Button from "../../coreui/Button";
import { EditMask as EditMaskBase } from "../../coreui/EditMask";
import { ErrorBadge } from "../../coreui/ErrorBadge";
import Icon from "../../coreui/Icon";
import MultilineTextFieldDialog from "../../coreui/MultilineTextFieldDialog";
import Presentation from "../../coreui/Presentation";
import { TableChildProps } from "../../coreui/Table";
import {
  CellFocusUtil,
  FocusCellRendererParams,
} from "../../coreui/table/CellFocusUtil";
import { CellUtil } from "../../coreui/table/CellUtil";
import { RuntimeWidget } from "../../models/PaneRow";
import { AccessLevel } from "../AccessLevel";
import { FunctionName } from "../TableSummary";
import GridColumn from "./GridColumn";

interface ConfigProperties extends FocusCellRendererParams<string | null> {
  dataId: string;
  editMask?: string;
  justification?: "Left" | "Right";
  name: string;
  propagated: TableChildProps;
  showEllipsis?: boolean;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  bodyText?: string[];
}

interface State {
  isDialogOpen: boolean;
  isErrorBadgeOpen?: boolean;
}

const EntryHint = styled("div")(({ theme }) => ({
  color: theme.palette.grey[300],
  textAlign: "left",
}));

const ExpandButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
}));

export class TextColumn extends React.Component<ConfigProperties, State> {
  private readonly componentId: string;
  private readonly errorMessageId: string;
  public static getSummaryValue(
    runtimeData: RuntimeWidget[],
    configProperties: ConfigProperties,
    functionName: FunctionName
  ): string | null {
    let result = 0;
    for (const data of runtimeData) {
      if (data.value !== null && typeof data.value !== "string") {
        throw new Error(`Unexpected data type ${typeof data.value}`);
      }

      const runtimeProperties = data.properties as RuntimeProperties;

      const value: string | null = runtimeProperties.bodyText
        ? runtimeProperties.bodyText.join("\n")
        : data.value;

      if (value) {
        result += 1;
      }
    }

    return result.toString();
  }

  public constructor(props: ConfigProperties) {
    super(props);

    this.state = { isDialogOpen: false, isErrorBadgeOpen: false };

    CellFocusUtil.subscribeToCellKeyboardFocusedEvent(
      props,
      this.onCellFocus,
      this.onCellBlur
    );

    this.componentId = `text-column-${Sys.nextId}`;
    this.errorMessageId = `${this.componentId}-error-message`;

    props.eGridCell.addEventListener("keydown", this.onCellKeyDown);
  }

  private onCellBlur = (): void => {
    this.setState({ isErrorBadgeOpen: false });
  };

  private onCellFocus = (): void => {
    this.setState({ isErrorBadgeOpen: true });
  };

  private onCellKeyDown = (event: KeyboardEvent): void => {
    if (
      (event.key === "Enter" || event.key === " ") &&
      !this.props.column!.isCellEditable(this.props.node)
    ) {
      event.preventDefault();
      event.stopPropagation();

      this.setState({ isDialogOpen: true });

      return;
    }

    if (event.key === " " && !this.props.api.getEditingCells().length) {
      this.props.api.startEditingCell({
        colKey: this.props.column!.getColId(),
        rowIndex: this.props.node.rowIndex!,
      });

      event.preventDefault();
      event.stopPropagation();
    }

    if (
      event.key === "Tab" &&
      !!this.props.api.getEditingCells().length &&
      this.props.colDef?.cellEditor.widgetType !== "DateEditColumn" &&
      this.props.colDef?.cellEditor.widgetType !== "MLTextEditColumn"
    ) {
      this.props.api.stopEditing();

      if (event.shiftKey) {
        this.props.api.tabToPreviousCell();
      } else {
        this.props.api.tabToNextCell();
      }

      event.preventDefault();
      event.stopPropagation();
    }

    CellUtil.customizeGridNavigation(event, this.props);
  };

  private onCloseErrorBadge = (): void => {
    this.setState({ isErrorBadgeOpen: false });
  };

  private onDialogCancel = (): void => {
    this.setState({ isDialogOpen: false });
  };

  private onOpenErrorBadge = (): void => {
    this.setState({ isErrorBadgeOpen: true });
  };

  private showDialog = (): void => {
    this.setState({ isDialogOpen: true });
  };

  public componentWillUnmount(): void {
    CellFocusUtil.unsubscribeToCellKeyboardFocusedEvent(
      this.props,
      this.onCellFocus,
      this.onCellBlur
    );
    this.props.eGridCell.removeEventListener("keydown", this.onCellKeyDown);
  }

  public render(): React.ReactNode {
    const row = this.props.data!;

    let rowErrors: string | undefined = this.props.propagated.parentTable
      .getTable()
      .rowErrorMessages.get(row.rowKey);

    const widget = row.getWidgetT<string | null, RuntimeProperties>(
      this.props.name
    );

    CellUtil.setReadOnlyAttribute(
      this.props.eGridCell,
      widget.properties.accessLevel <= AccessLevel.actionable
    );

    let value: string | null = widget.properties.bodyText
      ? widget.properties.bodyText.join("\n")
      : this.props.value || null;

    const errors: string[] = this.props.colDef!.cellEditor
      ? this.props.colDef!.cellEditor.getErrors(
          this.props.colDef!.cellEditorParams,
          row,
          value
        )
      : [];

    value =
      this.props.editMask && value
        ? EditMaskBase.formatValue(this.props.editMask, value)
        : value;

    let expandButton: React.ReactNode = null;
    let expandButtonDialog: React.ReactNode = null;
    if (value && !this.props.showEllipsis) {
      const columnWidth = this.props.eGridCell.getBoundingClientRect().width;
      const textWidth: number = Presentation.measureText(value);

      // If its enterable we need to ajust the size for the pencil.
      if (
        (widget.properties.accessLevel >= AccessLevel.enterable &&
          textWidth > columnWidth - 67) ||
        (widget.properties.accessLevel < AccessLevel.enterable &&
          textWidth > columnWidth - 50)
      ) {
        expandButton = (
          <ExpandButton
            aria-hidden
            customSize="tiny"
            icon="fas fa-ellipsis-h"
            onClick={this.showDialog}
            onMouseDown={() => (GridColumn.suppressEdit = true)}
            tabIndex={-1}
          />
        );

        expandButtonDialog = (
          <MultilineTextFieldDialog
            label={this.props.colDef!.headerName}
            onCancel={this.onDialogCancel}
            open={this.state.isDialogOpen}
            readOnly={true}
            value={value}
          />
        );
      }
    }

    let result: React.ReactNode = null;

    const alignment = this.props.justification === "Right" ? "right" : "left";

    if (widget.properties.accessLevel >= AccessLevel.enterable) {
      const entryHint: React.ReactNode = value ? null : (
        <EntryHint>
          {Localization.getBuiltInMessage("editDetailsEllipses")}
        </EntryHint>
      );

      if (errors.length) {
        CellUtil.setAriaAttributes(
          this.props.eGridCell,
          this.errorMessageId,
          true
        );

        rowErrors = `${errors.join(". ")}. ${rowErrors || ""}`;

        result = (
          <ErrorBadge
            errors={errors}
            isShort={
              row.isNew && this.props.propagated.parentTable.isDocumentGrid
            }
            onClose={this.onCloseErrorBadge}
            onOpen={this.onOpenErrorBadge}
            open={this.state.isErrorBadgeOpen}
            suppressEdit={true}
          >
            <div
              aria-describedby={this.errorMessageId}
              aria-errormessage={this.errorMessageId}
              aria-invalid="true"
              role="textbox"
              style={{
                alignItems: "center",
                display: "flex",
                flex: "auto",
                height: "100%",
                margin: "0 24px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  flex: "auto",
                  lineHeight: "normal",
                  overflow: "hidden",
                  textAlign: alignment,
                  textOverflow: this.props.showEllipsis ? "ellipsis" : "clip",
                  whiteSpace: "nowrap",
                }}
              >
                {value}
                {entryHint}
              </div>
              {expandButton}
              {expandButtonDialog}
              <div className="cx-print">
                <Icon
                  fixedWidth
                  icon="fas fa-pencil"
                  style={{
                    color: colors.grey[800],
                    fontSize: 12,
                    marginLeft: 8,
                  }}
                />
              </div>
            </div>
            <div id={this.errorMessageId} style={{ display: "none" }}>
              {rowErrors}
            </div>
          </ErrorBadge>
        );
      } else {
        CellUtil.setAriaAttributes(
          this.props.eGridCell,
          this.errorMessageId,
          !!rowErrors
        );

        result = (
          <div
            aria-describedby={rowErrors ? this.errorMessageId : undefined}
            aria-errormessage={rowErrors ? this.errorMessageId : undefined}
            aria-invalid={rowErrors ? true : false}
            role="textbox"
            style={{
              alignItems: "center",
              display: "flex",
              height: "100%",
              margin: "0 24px",
            }}
          >
            <div
              style={{
                flex: "auto",
                lineHeight: "normal",
                overflow: "hidden",
                textAlign: alignment,
                textOverflow: this.props.showEllipsis ? "ellipsis" : "clip",
                whiteSpace: "nowrap",
              }}
            >
              {value}
              {entryHint}
            </div>
            {expandButton}
            {expandButtonDialog}
            <div className="cx-print">
              <Icon
                fixedWidth
                icon="fas fa-pencil"
                style={{
                  color: colors.grey[800],
                  display: "inline",
                  fontSize: 12,
                  height: "auto",
                  marginLeft: 8,
                }}
              />
            </div>
            <div id={this.errorMessageId} style={{ display: "none" }}>
              {rowErrors}
            </div>
          </div>
        );
      }
    } else {
      CellUtil.setAriaAttributes(
        this.props.eGridCell,
        this.errorMessageId,
        !!rowErrors
      );

      result = (
        <div
          aria-describedby={rowErrors ? this.errorMessageId : undefined}
          aria-errormessage={rowErrors ? this.errorMessageId : undefined}
          aria-invalid={rowErrors ? true : false}
          role="textbox"
          style={{
            alignItems: "center",
            display: "flex",
            height: "100%",
            margin: "0px 24px",
          }}
        >
          <div
            style={{
              lineHeight: "normal",
              overflow: "hidden",
              textAlign: alignment,
              textOverflow: this.props.showEllipsis ? "ellipsis" : "clip",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {value}
          </div>
          {expandButton}
          {expandButtonDialog}
          <div id={this.errorMessageId} style={{ display: "none" }}>
            {rowErrors}
          </div>
        </div>
      );
    }

    return result;
  }
}

export default observer(TextColumn);
